<template>
  <DropZoneFileUpload
    :accept-multiple-files="true"
    :accepted-file-types="['.ris']"
    @files-dropped="addCitationFiles"
  >
    <template #default="{ hasFileDraggedOver, hasFileOverWindow, triggerFilePicker }">
      <button
        class="w-full flex justify-between items-start rounded-lg border-dashed border-2 bg-white border-blue-400 p-4 space-y-2 text-left"
        :class="{
          '!border-blue-500': hasFileDraggedOver || hasFileOverWindow,
          '!bg-primary-500/5': hasFileOverWindow && !hasFileDraggedOver,
          '!bg-primary-500/10': hasFileDraggedOver,
        }"
        @click="triggerFilePicker"
      >
        <div class="space-y-2">
          <h2 class="font-medium text-sm text-blue-800 space-y-1">
            <div>
              Choose an RIS file
              <span v-if="isRequired" class="text-red-600">*</span>
            </div>
            <div class="text-xs text-slate-500">or drag it here.</div>
          </h2>
          <div v-if="citationFiles.length > 0" class="flex gap-2 flex-wrap">
            <div
              v-for="(citationFile, index) in citationFiles"
              :key="citationFile.file.name + index"
              class="flex items-center bg-slate-200 p-[6px] rounded-md leading-none text-xs text-slate-600 gap-1"
              :class="{
                'text-black': citationFile.isValid,
                'text-red-600': !citationFile.isValid,
              }"
            >
              {{ citationFile.file.name }}
              <button
                class="hover:text-black text-black/60"
                @click.stop="removeCitationFile(citationFile.file)"
              >
                <XMarkIcon class="w-3 h-3 stroke-2" />
              </button>
            </div>
          </div>
          <div v-if="citationFiles.length > 0 && citationFilesError" class="text-red-600 text-xs">
            {{ citationFilesError }}
          </div>
        </div>
        <BigDocumentTextIcon class="text-slate-300" />
      </button>
    </template>
  </DropZoneFileUpload>
</template>
<script setup lang="ts">
import DropZoneFileUpload from "@app/components/Global/Inputs/DropZoneFileUpload.vue";
import BigDocumentTextIcon from "@app/components/Icons/BigDocumentTextIcon.vue";
import XMarkIcon from "@app/components/Icons/XMarkIcon.vue";
import { watchEffect } from "vue";

defineProps<{
  isRequired?: boolean;
}>();

const citationFiles = defineModel<{ isValid: boolean; file: File }[]>({
  default: [],
});

function removeCitationFile(file: File) {
  citationFiles.value = citationFiles.value.filter((f) => f.file !== file);
}
const citationFilesError = defineModel<string>("error");

function addCitationFiles(files: File[]) {
  files.forEach((file) => {
    if (
      !citationFiles.value.some((cf) => cf.file.name === file.name && cf.file.size === file.size)
    ) {
      const reader = new FileReader();
      reader.onload = (e) => {
        let isValid = false;
        const content = e.target?.result;
        if (typeof content === "string" && validateRISFile(content)) {
          isValid = true;
        }
        citationFiles.value.push({ file, isValid });
      };
      reader.readAsText(file);
    }
  });
}
watchEffect(() => {
  citationFilesError.value = citationFiles.value.every((cf) => cf.isValid)
    ? ""
    : "some of the files are invalid";
}, {});

function validateRISFile(content: string): boolean {
  const normalizedContent = content.replace(/\r\n|\xa0/gm, "\n");
  const studyRegex = /(.*?)ER\s{2}-/gs;
  const rawStudies = normalizedContent.match(studyRegex)?.map(String);

  if (!rawStudies) return false;

  return rawStudies.every((record) => {
    const startsWithValidType = /^TY\s+-\s/m.test(record);
    return startsWithValidType && record.trim() !== "";
  });
}
</script>
